import { GetServerSideProps, NextPage } from 'next'
import { unstable_serialize } from 'swr'
import { client } from '~/api/client'
import {
  Schemas,
  getMemberByUserName,
  getMembersUserNameFoldersV1,
  getMembersUserNameSellProductsV1,
} from '~/api/gen'
import { Layout } from '~/components/Layout'
import { Meta } from '~/components/Meta'
import { MemberFolder } from '~/features/MemberFolder/MemberFolder'
import { FETCH_COUNT_PER_PAGE } from '~/features/MemberFolder/constants'
import { getMetadata } from '~/features/MemberFolder/lib/getMetadata'
import { buildMemberDlink } from '~/features/shared/memberDetail/lib/buildMemberDlink'
import { getProductsFetchKeyByName } from '~/hooks/useSellProductsByUserName'
import { getPageNumberFromQueryValue } from '~/lib/getPageNumberFromQueryValue'

export type Props = {
  member: Schemas.member_large_detail
  folderCollection: Schemas.get_members_user_name_folders_v1_response
  pageNumber: number
}
const MemberFolderPage: NextPage<Props> = ({
  member,
  folderCollection,
  pageNumber,
}) => {
  const breadcrumbList = [
    member.shop
      ? {
          name: member.shop.name,
          path: `/shop/${member.shop.id}/`,
        }
      : [],
    {
      name: member.nick_name,
      path: `/${member.user_name}/`,
    },
    {
      name: 'お気に入りフォルダ一覧',
    },
  ].flat()

  const isFirstPage = pageNumber === 1

  return (
    <Layout
      breadcrumbList={breadcrumbList}
      headerProps={{
        headerType: 'border',
        appLink: `wear2020://wear.jp/user/${member.id}/favorite/`,
        hideAppIcon: isFirstPage,
      }}
      appUrl={`wear2020://wear.jp/user/${member.id}/favorite/`}
      appDownloadUrl={buildMemberDlink(member.id)}
      abstractUrl="wear.jp/username/favorite/"
    >
      <Meta {...getMetadata(member, folderCollection.folder_total_count)} />
      <MemberFolder member={member} folderCollection={folderCollection} />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
}) => {
  const userName = query.username as string
  const { pageNumber, isValid } = getPageNumberFromQueryValue(query.pageno)

  if (!isValid) {
    return {
      redirect: {
        statusCode: 301,
        destination: `/${userName}/favorite/`,
      },
    }
  }
  const [memberResponse, foldersResponse, sellProductsResponse] =
    await Promise.all([
      getMemberByUserName(client, {
        parameter: {
          user_name: userName,
        },
      }),
      getMembersUserNameFoldersV1(client, {
        parameter: {
          user_name: userName,
          pagesize: FETCH_COUNT_PER_PAGE,
          pageno: pageNumber,
        },
      }),
      getMembersUserNameSellProductsV1(client, {
        parameter: {
          user_name: userName,
          limit: 1,
        },
      }),
    ])

  if (memberResponse.type === 'error') {
    console.error(memberResponse.error)
    if (memberResponse.raw.status === 404) return { notFound: true }
    throw new Error('Failed to get member')
  }

  if (foldersResponse.type === 'error') {
    if (foldersResponse.raw.status === 404) return { notFound: true }
    throw new Error(
      `Failed to fetch folders user:${userName} ${foldersResponse.raw.status}`
    )
  }

  const sellProducts =
    sellProductsResponse.type === 'success'
      ? sellProductsResponse.data
      : {
          products: [],
          total_count: 0,
          is_y_connect_cooperation: false,
          next_offset: null,
        }

  const folderCollection = foldersResponse.data
  if (pageNumber > 1 && folderCollection.folders.length === 0) {
    return {
      redirect: {
        statusCode: 301,
        destination: `/${userName}/favorite/`,
      },
    }
  }

  return {
    props: {
      fallback: {
        [unstable_serialize(getProductsFetchKeyByName(userName, 1))]:
          sellProducts,
      },
      member: memberResponse.data,
      folderCollection,
      pageNumber,
    },
  }
}
export default MemberFolderPage
